import { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from '../services/supabase'
import { verifyPassword } from '../utils/password'
import { logLogin, logLogout, logActivity } from '../utils/activityLogger'

// Geçici çözüm: Service role key kullan (sadece test için)
// NOT: Production'da bu kullanılmamalı!
try {
  const serviceKey = process.env.REACT_APP_SUPABASE_SERVICE_KEY
  if (serviceKey) {
    // Service key varsa onu kullan
    console.log('Service role key kullanılıyor (TEST MODU)')
  }
} catch (e) {
  console.log('Service role key bulunamadı, anon key kullanılıyor')
}

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

const STORAGE_KEY = 'meb_auth_user'
const LOGIN_LOG_KEY = 'meb_login_log_id'

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // LocalStorage'dan mevcut oturumu kontrol et
    const storedUser = localStorage.getItem(STORAGE_KEY)
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser)
        setUser(userData)
        fetchUserProfile(userData.id)
      } catch (error) {
        console.error('Oturum yüklenemedi:', error)
        localStorage.removeItem(STORAGE_KEY)
        setLoading(false)
      }
    } else {
      setLoading(false)
    }
  }, [])

  const fetchUserProfile = async (userId) => {
    try {
      // Önce is_active kontrolü olmadan dene
      let { data, error } = await supabase
        .from('users')
        .select(`
          id,
          email,
          full_name,
          phone,
          role_id,
          school_id,
          is_active,
          role:roles(*),
          school:schools(*)
        `)
        .eq('id', userId)
        .single()

      // Eğer is_active false ise, yine de kullanıcıyı yükle (sadece uyarı ver)
      if (data && !data.is_active) {
        console.warn('Kullanıcı aktif değil:', data.email)
      }

      if (error) throw error
      if (!data) throw new Error('Kullanıcı bulunamadı')
      
      setUserProfile(data)
      // User bilgisini de güncelle
      setUser({ id: data.id, email: data.email })
      
      // LocalStorage'a kaydet (impersonate bilgilerini koru)
      const currentStorage = localStorage.getItem(STORAGE_KEY)
      let storageData = { id: data.id, email: data.email }
      
      if (currentStorage) {
        try {
          const currentData = JSON.parse(currentStorage)
          // Eğer impersonate durumu varsa, onu koru
          if (currentData.impersonated && currentData.originalUser) {
            storageData.impersonated = true
            storageData.originalUser = currentData.originalUser
            console.log('fetchUserProfile - Impersonate bilgileri korundu:', storageData)
          }
        } catch (e) {
          console.error('fetchUserProfile - Storage parse hatası:', e)
        }
      }
      
      localStorage.setItem(STORAGE_KEY, JSON.stringify(storageData))
      console.log('fetchUserProfile - localStorage güncellendi:', storageData)
    } catch (error) {
      console.error('Kullanıcı profili yüklenemedi:', error)
      localStorage.removeItem(STORAGE_KEY)
      setUser(null)
      setUserProfile(null)
    } finally {
      setLoading(false)
    }
  }

  const signIn = async (email, password) => {
    try {
      console.log('Login attempt:', email)
      
      // Users tablosundan kullanıcıyı bul
      const { data, error } = await supabase
        .from('users')
        .select(`
          id,
          email,
          password,
          full_name,
          phone,
          role_id,
          school_id,
          is_active,
          role:roles(*),
          school:schools(*)
        `)
        .eq('email', email)
        .single()

      console.log('Query result:', { data, error, isActive: data?.is_active })

      // RLS hatası kontrolü
      if (error) {
        if (error.code === '42501' || error.code === 'PGRST301' || 
            error.message?.includes('permission') || 
            error.message?.includes('row-level') ||
            error.message?.includes('permission denied')) {
          console.error('RLS/Permission hatası:', error)
          throw new Error(
            'Veritabanı erişim hatası. Lütfen Supabase SQL Editor\'da şu komutu çalıştırın:\n\n' +
            'ALTER TABLE users DISABLE ROW LEVEL SECURITY;\n\n' +
            'VEYA supabase/fix-rls-complete.sql dosyasını çalıştırın.'
          )
        }
        console.error('Login hatası:', error)
        throw new Error(error.message || 'Kullanıcı bulunamadı veya aktif değil')
      }

      if (!data) {
        throw new Error('Kullanıcı bulunamadı')
      }

      // is_active kontrolü (uyarı ver ama engelleme)
      if (!data.is_active) {
        console.warn('Kullanıcı aktif değil, yine de giriş yapılıyor:', data.email)
        // İsterseniz burada throw new Error('Kullanıcı aktif değil') yapabilirsiniz
      }

      console.log('User found, checking password...')

      // Şifre kontrolü (hem plain text hem hash'lenmiş)
      const isPasswordValid = verifyPassword(password, data.password)

      if (!isPasswordValid) {
        console.error('Password mismatch')
        throw new Error('E-posta veya şifre hatalı')
      }

      console.log('Password verified, login successful')

      // Şifreyi response'dan çıkar (güvenlik)
      const { password: _, ...userData } = data

      // Kullanıcıyı set et
      setUser({ id: userData.id, email: userData.email })
      setUserProfile(userData)
      
      // LocalStorage'a kaydet (impersonate bilgilerini koru)
      const currentStorage = localStorage.getItem(STORAGE_KEY)
      let storageData = { id: userData.id, email: userData.email }
      
      if (currentStorage) {
        try {
          const currentData = JSON.parse(currentStorage)
          // Eğer impersonate durumu varsa, onu koru
          if (currentData.impersonated && currentData.originalUser) {
            storageData.impersonated = true
            storageData.originalUser = currentData.originalUser
          }
        } catch (e) {
          // Ignore
        }
      }
      
      localStorage.setItem(STORAGE_KEY, JSON.stringify(storageData))

      // Login log kaydet
      try {
        const userAgent = navigator.userAgent
        const loginLog = await logLogin(userData.id, null, userAgent)
        if (loginLog) {
          localStorage.setItem(LOGIN_LOG_KEY, loginLog.id)
        }
        
        // Activity log kaydet
        await logActivity(
          userData.id,
          'user_login',
          'user',
          userData.id,
          `Kullanıcı giriş yaptı: ${userData.email}`
        )
      } catch (error) {
        console.error('Log kaydı hatası:', error)
      }

      return { user: userData }
    } catch (error) {
      console.error('Sign in error:', error)
      throw error
    }
  }

  const signOut = async () => {
    // Logout log kaydet
    try {
      const loginLogId = localStorage.getItem(LOGIN_LOG_KEY)
      const currentUser = user || userProfile
      if (loginLogId && currentUser) {
        await logLogout(loginLogId, currentUser.id)
        
        // Activity log kaydet
        await logActivity(
          currentUser.id,
          'user_logout',
          'user',
          currentUser.id,
          `Kullanıcı çıkış yaptı: ${currentUser.email}`
        )
      }
    } catch (error) {
      console.error('Logout log hatası:', error)
    }
    
    localStorage.removeItem(STORAGE_KEY)
    localStorage.removeItem(LOGIN_LOG_KEY)
    setUser(null)
    setUserProfile(null)
  }

  // Yönetici için başka kullanıcı olarak oturum açma (impersonate)
  const impersonateUser = async (targetUserId) => {
    try {
      // Mevcut kullanıcıyı kaydet (eğer yoksa userProfile'dan al)
      const currentUser = user || { id: userProfile?.id, email: userProfile?.email }
      
      // Hedef kullanıcının bilgilerini al
      const { data, error } = await supabase
        .from('users')
        .select(`
          id,
          email,
          full_name,
          phone,
          role_id,
          school_id,
          is_active,
          role:roles(*),
          school:schools(*)
        `)
        .eq('id', targetUserId)
        .single()

      if (error) throw error
      if (!data) throw new Error('Kullanıcı bulunamadı veya aktif değil')

      // Kullanıcıyı set et
      setUser({ id: data.id, email: data.email })
      setUserProfile(data)
      
      // LocalStorage'a kaydet (impersonate flag ile)
      const storageData = { 
        id: data.id, 
        email: data.email,
        impersonated: true,
        originalUser: currentUser ? { id: currentUser.id, email: currentUser.email } : null
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(storageData))

      console.log('Impersonate başarılı:', { 
        targetUser: data.email, 
        originalUser: currentUser?.email,
        storageData: storageData,
        localStorageCheck: localStorage.getItem(STORAGE_KEY)
      })
      
      // Storage event trigger et (diğer componentlerin güncellenmesi için)
      // Aynı tab'da storage event çalışmaz, bu yüzden custom event kullanıyoruz
      window.dispatchEvent(new CustomEvent('localStorageChange', {
        detail: { key: STORAGE_KEY, value: storageData }
      }))

      return { user: data }
    } catch (error) {
      console.error('Impersonate error:', error)
      throw error
    }
  }

  // Orijinal kullanıcıya geri dön
  const stopImpersonating = async () => {
    const storedUser = localStorage.getItem(STORAGE_KEY)
    console.log('stopImpersonating çağrıldı, storedUser:', storedUser)
    
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser)
        console.log('Parsed userData:', userData)
        
        if (userData.originalUser) {
          console.log('Orijinal kullanıcı bulundu:', userData.originalUser)
          
          // Orijinal kullanıcının bilgilerini al
          const { data, error } = await supabase
            .from('users')
            .select(`
              id,
              email,
              full_name,
              phone,
              role_id,
              school_id,
              is_active,
              role:roles(*),
              school:schools(*)
            `)
            .eq('id', userData.originalUser.id)
            .single()

          console.log('Orijinal kullanıcı query sonucu:', { data, error })

          if (error) throw error
          if (!data) throw new Error('Orijinal kullanıcı bulunamadı')

          // Orijinal kullanıcıyı set et
          setUser({ id: data.id, email: data.email })
          setUserProfile(data)
          
          // LocalStorage'dan impersonate flag'ini kaldır
          const newStorageData = { 
            id: data.id, 
            email: data.email
          }
          localStorage.setItem(STORAGE_KEY, JSON.stringify(newStorageData))
          
          console.log('Orijinal kullanıcıya dönüldü:', data.email)
          console.log('Yeni localStorage:', newStorageData)
          
          // Storage event trigger et
          window.dispatchEvent(new CustomEvent('localStorageChange', {
            detail: { key: STORAGE_KEY, value: newStorageData }
          }))
        } else {
          console.warn('originalUser bulunamadı, direkt çıkış yapılıyor')
          // Eğer originalUser yoksa, direkt çıkış yap
          localStorage.removeItem(STORAGE_KEY)
          setUser(null)
          setUserProfile(null)
        }
      } catch (error) {
        console.error('Stop impersonating error:', error)
        throw error
      }
    } else {
      console.warn('storedUser bulunamadı')
    }
  }

    const value = {
    user,
    userProfile,
    loading,
    signIn,
    signOut,
    impersonateUser,
    stopImpersonating,
    refreshProfile: () => user && fetchUserProfile(user.id),
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}



